import { createApiClient } from '@fewerbags/api-client';

/** Fired (on window) when any API call returns 401 — the AuthProvider clears state and redirects. */
export const UNAUTHORIZED_EVENT = 'fewerbags:unauthorized';

/**
 * Single API client for the web app.
 * Dev: '' → Vite proxies /api, /healthz, /readyz to the API (vite.config.ts).
 * Prod: '' → nginx serves the SPA and proxies the same paths (PLAN §7).
 * Set VITE_API_BASE only when the API lives on another origin.
 */
export const api = createApiClient({
  baseUrl: import.meta.env.VITE_API_BASE ?? '',
  onUnauthorized: () => {
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
  },
});

export const queryKeys = {
  readyz: ['server', 'readyz'] as const,
  healthz: ['server', 'healthz'] as const,
  me: ['auth', 'me'] as const,
  authConfig: ['auth', 'config'] as const,
  sessions: ['auth', 'sessions'] as const,
  passkeys: ['auth', 'passkeys'] as const,
  members: ['household', 'members'] as const,
  people: ['household', 'people'] as const,
  invites: ['household', 'invites'] as const,
  equipment: ['household', 'equipment'] as const,
  householdMe: ['household', 'me'] as const,
  adminHouseholds: (q: string, includeDisabled: boolean) =>
    ['admin', 'households', q, includeDisabled] as const,
  adminAudit: (householdId: string | null) => ['admin', 'audit', householdId] as const,
  pantryList: (query: NonNullable<Parameters<typeof api.pantry.list>[0]>) =>
    ['pantry', 'list', query] as const,
  pantryExpiring: (days: number) => ['pantry', 'expiring', days] as const,
  /** Meal-plan screens (Phase 2). Everything lives under a single `plans` prefix
   *  so one `invalidateQueries({ queryKey: queryKeys.plans.all })` refreshes the
   *  week grid, the plan editor, its timeline and the suggestion panel together. */
  plans: {
    all: ['plans'] as const,
    week: (from: string, to: string) => ['plans', 'week', from, to] as const,
    detail: (id: string) => ['plans', 'detail', id] as const,
    timeline: (id: string, serveAt?: string | undefined, now?: string | undefined) =>
      ['plans', 'timeline', id, serveAt ?? null, now ?? null] as const,
    /** Prefix of every timeline query for one plan (any serveAt/now override). */
    timelinesFor: (id: string) => ['plans', 'timeline', id] as const,
    cookedPreview: (id: string) => ['plans', 'cookedPreview', id] as const,
    suggestions: (date: string, slot: string, windowDays: number) =>
      ['plans', 'suggestions', date, slot, windowDays] as const,
    /** Full recipe detail (steps included) for every recipe on a plan, for the print view. */
    printRecipes: (id: string, recipeIds: readonly string[]) =>
      ['plans', 'printRecipes', id, ...recipeIds] as const,
  },
  ingredientSearch: (q: string) => ['ingredients', 'search', q] as const,
  ingredientDetail: (id: string) => ['ingredients', 'detail', id] as const,
  ingredientCategories: ['ingredients', 'categories'] as const,
};
